import { BreakpointObserver } from '@angular/cdk/layout';
import {
  Component,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges
} from '@angular/core';
import { Router } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { filter as _filter, find as _find, first as _first, get as _get } from 'lodash';
import { combineLatest, Observable, Subject } from 'rxjs';
import { filter, first, map, mergeMap, startWith, take, takeUntil, tap } from 'rxjs/operators';

import { env } from '@amfam/environment';
import { PlautoService, PolicySelectors } from '@amfam/policy/data-access';
import { PolicyUtilService } from '@amfam/policy/e-signature/data-access';
import {
  AutoCategoryType,
  AutoPolicy,
  CoverageDeductibleLimit,
  GracePeriodDays,
  Policy,
  PolicyDetails,
  RiskModel,
  RiskSelectionContext,
  SourceSystemType,
  Vehicle
} from '@amfam/policy/models';
import { collisionCovergeCodes, comprehensiveCoverageCodes } from '@amfam/policy/shared';
import { VechiclePolicyChangeEntryPoint, VehicleFacade } from '@amfam/policy/vehicle/data-access';
import { AnalyticsFacade } from '@amfam/shared/analytics';
import { PolicyChangeRequestType } from '@amfam/shared/models';
import { userQuery } from '@amfam/shared/user';
import { agentQuery } from '@amfam/shared/utility/agent';
import { BrandSelectors } from '@amfam/shared/utility/brand';
import { FeatureFlagService } from '@amfam/shared/utility/feature-flag/data-access';
import { impersonationQuery } from '@amfam/shared/utility/impersonation';
import { fromRouterActions } from '@amfam/shared/utility/navigation';
import { CopyService, ScrollService } from '@amfam/shared/utility/shared-services';

import * as fromRoot from '../../../core/store';
import * as replaceVehiclePolicyActions from '../../../core/store/replace-vehicle-policy/replace-vehicle-policy.actions';
import * as replaceVehicleActions from '../../../core/store/replace-vehicle/replace-vehicle.actions';

@Component({
  selector: 'auto-risk-card',
  templateUrl: './auto-risk-card.component.html',
  styleUrls: ['./auto-risk-card.component.scss', '../policy-risk-card.scss'],
  standalone: false
})
export class AutoRiskCardComponent implements OnInit, OnDestroy, OnChanges {
  @Input() vehicleId: string;
  @Input() multipleVehicle = false;
  @Input() isConnectPartner = false;

  public advancedAutoPolicies: RiskModel[] = new Array<RiskModel>();
  public policy: AutoPolicy;
  public vehicle: Vehicle;
  public coverages: CoverageDeductibleLimit[] = new Array<CoverageDeductibleLimit>();
  public additionalCoverages: { coverageName: string; limit: string }[] = [];
  public thirdPartyInterest = [];
  public displayKyd = false;
  public kydEnrolled = false;
  public kydStatus: string;
  public kydEnrollmentId: number;
  public amfamDotCom: string;
  public displayPolicyGracePeriod = false;
  public autoCategoryType = AutoCategoryType;
  public waitingPeriodEndDate: string;
  public displayPGP = true;
  public hasDeductables = false;
  public isAdvancedCustomer: boolean;
  public isSmallScreen = false;

  public gracePeriodDays$: Observable<string>;

  advancedAutoPolicies$ = this.store.pipe(
    select(
      fromRoot.getCoverageChangeRisks,
      this.featureFlagService.isEnabled('kyd_model_coverage') ? [] : env.modelCoverageDisabledStates
    )
  );

  changeCoverageEnabled$ = this.store
    .select(BrandSelectors.selectIsPartner)
    .pipe(
      map(isPartner =>
        isPartner
          ? this.featureFlagService.isEnabled('partner_model_coverage')
          : this.featureFlagService.isEnabled('model_coverage')
      )
    );
  public isAdvanceAutoPolicy$: Observable<boolean>;
  public isShowMyCoverage = false;
  public showMobileViewGetInsuranceCardButton$ = false;

  public hasCompAndCollisonCoverage$: Observable<boolean>;
  public isImpersonating$ = this.store.select(impersonationQuery.isImpersonating);
  public activeAgents$ = combineLatest(
    this.store.select(fromRoot.getAllActiveAgents),
    this.store.select(agentQuery.getAreAllAgentsLoaded),
    (agents, loaded) => ({ agents, loaded })
  ).pipe(
    filter(data => !!data.loaded),
    map(data => data.agents)
  );

  public userState$ = this.store.select(userQuery.selectUserState);
  public $hidePartnerButton = combineLatest([
    this.store.select(userQuery.selectUserStateCode),
    this.store.select(BrandSelectors.selectIsPartner)
  ]).pipe(
    map(([userStateCode, isPartner]) => {
      let unsupportedStates = this.copyService.getCopy(
        'policy.changeCoverage.updateCoverageUnsupportedStates'
      );
      if (!isPartner) {
        unsupportedStates = this.copyService.getCopy(
          'policy.changeCoverage.updateCoverageAFIUnsupportedStates'
        );
      }
      return isPartner && unsupportedStates.includes(userStateCode.toLowerCase());
    })
  );
  rentalReimbursementValue: string;

  private currentVehicle: RiskModel;
  private risks: RiskModel[];

  private customerId: string;
  private kydEligible = false;

  // Shared subject for completing observables
  private stop$: Subject<void> = new Subject<void>();

  addressChangeState: any = { changeRequestType: PolicyChangeRequestType.ADDRESS_CHANGE };
  vinChangeState: any = { changeRequestType: PolicyChangeRequestType.VIN_CHANGE };
  requestChangeNewTemplate = this.featureFlagService.isEnabled('request_change_new_template');
  eligibleForVinChange;

  constructor(
    private autoPolicyService: PlautoService,
    private store: Store<fromRoot.RootState>,
    private featureFlagService: FeatureFlagService,
    private policyUtilService: PolicyUtilService,
    public copyService: CopyService,
    private featureService: FeatureFlagService,
    private breakPointObservable: BreakpointObserver,
    private scrollService: ScrollService,
    private router: Router,
    private analyticsFacade: AnalyticsFacade,
    private vehicleFacade: VehicleFacade
  ) {}
  @HostListener('window:resize')
  resize() {
    this.screenSize();
  }
  screenSize() {
    this.isSmallScreen = this.breakPointObservable.isMatched(
      '(max-width: 480px) and (orientation: portrait)'
    );
    this.showMobileViewGetInsuranceCardButton$ = this.breakPointObservable.isMatched(
      '(max-width: 480px) and (orientation: portrait)'
    );
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (this.vehicleId) {
      this.addressChangeState = { ...this.addressChangeState, vehicleId: this.vehicleId };
      this.store
        .select(PolicySelectors.selectSelectedPolicy)
        .pipe(
          filter(policy => !!policy),
          first(),
          tap(policy => this.onPolicyChange(policy))
        )
        .subscribe();
    }
  }

  ngOnInit() {
    this.isSmallScreen = this.breakPointObservable.isMatched(
      '(max-width: 480px) and (orientation: portrait)'
    );
    this.showMobileViewGetInsuranceCardButton$ = this.breakPointObservable.isMatched(
      '(max-width: 480px) and (orientation: portrait)'
    );
    this.amfamDotCom = env.links.amfamDotComUrl;

    combineLatest(
      this.store.select(userQuery.selectCustomerId),
      this.store.select(fromRoot.getReplaceVehicleRisks),
      (customerId, risks) => ({
        customerId: customerId,
        risks: risks
      })
    )
      .pipe(takeUntil(this.stop$))
      .subscribe(state => {
        this.customerId = state.customerId;
        this.risks = state.risks;
        this.currentVehicle = _get(this, 'risks', []).find(
          risk => risk.vehicleId === this.vehicleId
        );
        const periodStartDate: Date = _get(this.currentVehicle, 'periodStartDate');
        this.isShowMyCoverage = periodStartDate.getTime() <= new Date().getTime() ? true : false;
        const data = {
          policynumber: _get(this.currentVehicle, 'policyNumber', ''),
          inEligibleStates: this.featureFlagService.isEnabled('kyd_model_coverage')
            ? []
            : env.modelCoverageDisabledStates || []
        };

        this.isAdvanceAutoPolicy$ = this.store.pipe(
          select(fromRoot.isMCEligibleStateAdvanceAutoPolicy, data),
          take(1)
        );
      });
    this.hasCompAndCollisonCoverage$ = this.store
      .select(fromRoot.hasSinglePolicyCompAndCollisonCoverageAndCarLightTruck, this.vehicleId)
      .pipe(take(1));

    this.gracePeriodDays$ = combineLatest([
      this.store.select(PolicySelectors.selectAdvanceAutoPolicies),
      this.store.select(userQuery.selectUserStateCode)
    ]).pipe(
      takeUntil(this.stop$),
      map((advancedAutoPolicies, userState) => {
        const advancedAutoPoliciesLength = _get(advancedAutoPolicies, 'length', 0);
        const isExtendedGracePeriodState = !!this.copyService
          .getCopy('policy.extendedGracePeriodStates')
          .find(state => state === userState);
        return advancedAutoPoliciesLength > 0 || !isExtendedGracePeriodState
          ? GracePeriodDays.REGULAR // 14
          : GracePeriodDays.EXTENDED; // 30
      })
    );
  }

  getInsuranceCard() {
    if (this.isSmallScreen) {
      this.analyticsFacade.trackButtonClick(PolicyDetails.buttonGetInsuranceCard);
      this.router.navigate(['insurance-card', this.vehicleId]);
    }
  }

  onClick() {
    if (this.isSmallScreen) {
      this.analyticsFacade.trackButtonClick(PolicyDetails.buttonFileAClaim);
      this.router.navigate(['/claims/report-claim-fnol']);
    } else {
      this.analyticsFacade.trackButtonClick(PolicyDetails.jumpLinkDocuments);
      this.scrollService.scrollToElement('documentsCard', 205);
    }
  }

  onPolicyChange(policy: Policy) {
    this.policy = <AutoPolicy>policy;
    if (this.policy) {
      this.vehicle = _first(_filter(this.policy.vehicles, { vehicleId: this.vehicleId }));
      if (this.vehicle) {
        this.vinChangeState = {
          ...this.vinChangeState,
          vehicleId: this.vehicleId,
          vehicleYear: this.vehicle.year
        };
        this.kydEligible = this.vehicle.vehicleTypeName === 'Car/Light Truck';
        this.eligibleForVinChange = this.vehicle.vehicleTypeName === 'Car/Light Truck';
        const vehicleCoverages = this.autoPolicyService
          .getRiskLevelCoverages(this.policy, this.vehicle, true)
          .map(coverage => {
            const deductible = _find(coverage.deductibleTerms, x => x.value !== '');
            const limit = _find(coverage.limitTerms, x => x.value !== '');
            return new CoverageDeductibleLimit(coverage, deductible, limit);
          });

        if (this.isConnectPartner) {
          // 'Physical Damage Group' coverage type is a deductable coverage
          this.coverages = _filter(
            vehicleCoverages,
            x =>
              !!x.limit ||
              (x.coverage.coverageType === 'Physical Damage Group' && !x.coverage.rejectedCoverage)
          );
        } else this.coverages = vehicleCoverages;

        for (const coverage of this.coverages) {
          if (coverage.deductible) {
            this.hasDeductables = true;
            break;
          }
        }

        this.additionalCoverages = _filter(
          vehicleCoverages,
          x => !x.deductible || x.coverage.coverageType !== 'Physical Damage Group'
        ).map(x => {
          const coverageName =
            this.policy.sourceSystem === SourceSystemType.Legacy
              ? x.coverage.description
              : x.coverage.name;
          const limit = this.getCoverageLimit(x.coverage.code, this.vehicle);
          return {
            coverageName,
            limit
          };
        });

        const thirdParty = _filter(this.vehicle.vehicleRoles, { roleType: 'ThirdPartyInterest' });
        if (thirdParty) {
          this.thirdPartyInterest = thirdParty;
        }
        if (this.kydEligible) {
          this.loadKydData();
        }
        const policyCoverages = this.autoPolicyService
          .getPolicyLevelCoverages(<AutoPolicy>this.policy, this.vehicleId)
          .map(coverage => {
            const deductible = _find(coverage.deductibleTerms, x => x.value !== '');
            const limit = _find(coverage.limitTerms, x => x.value !== '');
            return new CoverageDeductibleLimit(coverage, deductible, limit);
          });
        this.coverages = this.coverages.concat(policyCoverages);
        this.additionalCoverages = [];
        this.coverages = this.coverages.filter(c =>
          [...collisionCovergeCodes, ...comprehensiveCoverageCodes]
            .map(c => c.toUpperCase())
            .includes(c.coverage.code.toUpperCase())
        );
      }
    }
  }

  loadKydData() {
    if (this.policy.sourceSystem === SourceSystemType.Advance) {
      const enrollmentObservable = this.store.select(fromRoot.getKydEnrollments).pipe(
        mergeMap(enrollments => enrollments),
        filter(enrollment => enrollment.policyNumber === this.policy.policyNumber),
        mergeMap(enrollment => enrollment.enrolledVehicles),
        filter(vehicle => vehicle.vin === this.vehicle.vinSerialNumber),
        startWith(null)
      );
      const eligibilityObservable = this.store.select(fromRoot.getKydVehicleEligibility).pipe(
        mergeMap(kydEligibilities => kydEligibilities),
        filter(kydEligibility => kydEligibility.vin === this.vehicle.vinSerialNumber),
        startWith(null)
      );

      combineLatest(eligibilityObservable, enrollmentObservable)
        .pipe(map(latest => ({ eligibility: latest[0], enrollment: latest[1] })))
        .pipe(takeUntil(this.stop$))
        .subscribe(data => {
          const isEligible =
            data.eligibility && data.eligibility.isVehicleEligible && data.eligibility.canEnroll;
          if (data.enrollment && data.enrollment.enrollmentSummary) {
            const enrollmentSummary = data.enrollment.enrollmentSummary;
            this.kydStatus = enrollmentSummary.enrollmentStatus;

            if (this.kydStatus === 'Waiting Period' && enrollmentSummary.waitingPeriodEndDate) {
              this.displayKyd = true;
              this.waitingPeriodEndDate = enrollmentSummary.waitingPeriodEndDate;
            } else if (this.kydStatus === 'Enrolled') {
              this.displayKyd = true;
              this.kydEnrolled = true;
              this.kydEnrollmentId = enrollmentSummary.enrollmentId;
            } else {
              this.displayKyd = isEligible;
            }
          } else if (isEligible) {
            this.displayKyd = true;
          }
        });
    }
  }
  /*
    This method determines the
    Behavior of clicking the Add a Vehicle link on the template. Pop a modal or route change.
  */
  replaceVehicle() {
    this.analyticsFacade.trackButtonClick(PolicyDetails.buttonAddReplaceVehicle);
    this.store.dispatch(
      new replaceVehicleActions.SetVehicleRiskSelectionContext(RiskSelectionContext.REPLACE_VEHICLE)
    );
    this.store.dispatch(
      new replaceVehiclePolicyActions.ReplaceVehiclePolicySelectAction({
        vehicleId: _get(this.currentVehicle, 'id'),
        policyNumber: _get(this.currentVehicle, 'policy.policyNumber'),
        effectiveDate: _get(this.currentVehicle, 'policy.periodEndDate')
      })
    );
    const hasNonComprehensiveNonLiabilityCoverage = _get(
      this.currentVehicle,
      'vehicleCoverages',
      []
    ).some(coverage =>
      this.policyUtilService.isNonComprehensiveNonLiabilityCoverageVehicle(coverage)
    );

    combineLatest([
      this.store.select(fromRoot.isValidVehicleToBeReplaced(this.vehicleId)),
      this.store.select(BrandSelectors.selectIsPartner),
      this.store.select(PolicySelectors.selectHasClassicPolicies),
      this.store.select(PolicySelectors.selectHasAdvancePolicies),
      this.store.select(PolicySelectors.selectHasOnlyPolicyComprehensiveCoverage)
    ])
      .pipe(
        map(
          ([
            isValidVehicleToBeReplaced,
            isPartner,
            hasClassicPolicy,
            advancePolicy,
            hasOnlyPolicyComprehensiveCoverage
          ]) => ({
            isValidVehicleToBeReplaced,
            isPartner,
            hasClassicPolicy,
            advancePolicy,
            hasOnlyPolicyComprehensiveCoverage
          })
        ),
        take(1)
      )
      .subscribe(
        ({
          isValidVehicleToBeReplaced,
          isPartner,
          hasClassicPolicy,
          advancePolicy,
          hasOnlyPolicyComprehensiveCoverage
        }) => {
          const replaceVehicleSkipHardstop = this.featureFlagService.isEnabled(
            'replace_vehicle_skip_hardstop'
          );
          const isShowhardstopRiskSelectionPage =
            this.featureFlagService.isEnabled('add_vehicle') &&
            replaceVehicleSkipHardstop &&
            ((hasClassicPolicy && !advancePolicy && !isPartner) ||
              (advancePolicy && !isPartner && hasOnlyPolicyComprehensiveCoverage));

          if (isShowhardstopRiskSelectionPage && this.vehicle.vehicleId) {
            return this.store.dispatch(
              fromRouterActions.Go({
                path: [
                  `policies/risk-selection/replace-vehicle-quick-quote/${this.vehicle.vehicleId}/classic-year-vin`
                ]
              })
            );
          }
          if (!hasNonComprehensiveNonLiabilityCoverage || !isValidVehicleToBeReplaced) {
            return this.store.dispatch(
              fromRouterActions.Go({
                path: ['/policies/risk-selection', 'grace-period']
              })
            );
          }
          const pathName = this.featureFlagService.isEnabled('replace_vehicle_quickquote')
            ? '/policies/risk-selection/replace-vehicle-quick-quote'
            : '/policies/replace-vehicle';
          this.store.dispatch(
            fromRouterActions.Go({
              path: [pathName, this.vehicle.vehicleId]
            })
          );
        }
      );
  }

  addReplaceVehicle() {
    if (!this.isConnectPartner) {
      this.analyticsFacade.trackButtonClick(PolicyDetails.buttonAddReplaceVehicle);
      this.vehicleFacade.setVehiclePolicyChangeEntryPoint(VechiclePolicyChangeEntryPoint.POLICY);
      this.store.dispatch(
        new replaceVehicleActions.SetVehicleRiskSelectionContext(
          RiskSelectionContext.REPLACE_VEHICLE
        )
      );

      const vehicleId =
        this.advancedAutoPolicies.length > 1 ? '' : this.advancedAutoPolicies[0]?.vehicleId;

      // if multiple autoPolicies, route to risk-selection page
      if (this.advancedAutoPolicies.length > 1) {
        return this.store.dispatch(
          fromRouterActions.Go({
            path: ['/policies/risk-selection']
          })
        );
      }

      combineLatest([
        this.store.select(fromRoot.hasAnyPolicyCompAndCollisonCoverageAndCarLightTruck),
        this.store.select(fromRoot.isValidVehicleToBeReplaced(vehicleId)),
        this.store.select(BrandSelectors.selectIsPartner),
        this.store.select(PolicySelectors.selectHasClassicPolicies),
        this.store.select(PolicySelectors.selectHasAdvancePolicies),
        this.store.select(PolicySelectors.selectHasOnlyPolicyComprehensiveCoverage),
        this.store.select(fromRoot.hasAnyPolicyLiabilityCoverage)
      ])
        .pipe(
          map(
            ([
              isEligibleForAddVehicleFlow,
              isValidVehicleToBeReplaced,
              isPartner,
              hasClassicPolicy,
              advancePolicy,
              hasOnlyPolicyComprehensiveCoverage,
              hasAnyPolicyLiabilityCoverage
            ]) => ({
              isEligibleForAddVehicleFlow,
              isValidVehicleToBeReplaced,
              isPartner,
              hasClassicPolicy,
              advancePolicy,
              hasOnlyPolicyComprehensiveCoverage,
              hasAnyPolicyLiabilityCoverage
            })
          ),
          take(1)
        )
        .subscribe(
          ({
            isEligibleForAddVehicleFlow,
            isValidVehicleToBeReplaced,
            isPartner,
            hasClassicPolicy,
            advancePolicy,
            hasOnlyPolicyComprehensiveCoverage,
            hasAnyPolicyLiabilityCoverage
          }) => {
            let route = [];
            const addVehicleSkipHardstop = this.featureFlagService.isEnabled(
              'add_vehicle_skip_hardstop'
            );
            const isShowhardstopRiskSelectionPage =
              this.featureFlagService.isEnabled('add_vehicle') &&
              addVehicleSkipHardstop &&
              ((hasClassicPolicy && !advancePolicy && !isPartner) ||
                (advancePolicy && !isPartner && hasOnlyPolicyComprehensiveCoverage) ||
                hasAnyPolicyLiabilityCoverage);
            if (
              (isValidVehicleToBeReplaced ||
                (this.featureFlagService.isEnabled('add_vehicle') &&
                  !!isEligibleForAddVehicleFlow) ||
                isShowhardstopRiskSelectionPage) &&
              this.vehicle.vehicleTypeCode === 'auto'
            ) {
              route = ['/policies/risk-selection'];
            } else {
              route = ['/policies/risk-selection', 'grace-period'];
            }

            this.store.dispatch(
              fromRouterActions.Go({
                path: route
              })
            );
          }
        );
    } else {
      this.vehicleFacade.setVehiclePolicyChangeEntryPoint(VechiclePolicyChangeEntryPoint.POLICY);
      combineLatest([
        this.store.select(fromRoot.hasAnyPolicyCompAndCollisonCoverageAndCarLightTruck),
        // TODO: this will be removed when partners and non-partners
        this.store.select(BrandSelectors.selectIsPartner)
      ])
        .pipe(
          map(([isEligibleForAddVehicleFlow, isPartner]) => ({
            isEligibleForAddVehicleFlow,
            isPartner
          })),
          take(1)
        )
        .subscribe(({ isEligibleForAddVehicleFlow, isPartner }) => {
          let addVehicleRoute;

          if (
            isPartner &&
            this.featureFlagService.isEnabled('add_vehicle') &&
            isEligibleForAddVehicleFlow
          ) {
            addVehicleRoute = ['/policies/add-vehicle', 'expectation'];
          } else if (
            this.featureFlagService.isEnabled('add_vehicle') &&
            isEligibleForAddVehicleFlow
          ) {
            addVehicleRoute = ['/policies/risk-selection'];
          } else if (isPartner) {
            addVehicleRoute = ['/policies/add-vehicle/agent-help'];
          } else {
            addVehicleRoute = ['/policies/risk-selection', 'grace-period'];
          }

          this.store.dispatch(
            fromRouterActions.Go({
              path: addVehicleRoute
            })
          );
        });
    }
  }

  requestPolicyChange() {
    this.analyticsFacade.trackButtonClick(PolicyDetails.buttonRequestPolicyChange);

    this.router.navigate(['/policies/changerequest'], {
      state: { changeRequestType: PolicyChangeRequestType.POLICY_COVERAGE_CHANGE }
    });
  }

  coverageChange() {
    this.store
      .select(BrandSelectors.selectIsPartner)
      .pipe(first())
      .subscribe(isPartner => {
        this.store.dispatch(
          fromRouterActions.Go({
            path: ['/policies/change-coverage/' + this.vehicle.vehicleId + '/get-quote']
          })
        );
      });
  }

  policyGracePeriodModalChange(event) {
    this.displayPolicyGracePeriod = event;
  }

  ngOnDestroy() {
    this.stop$.next();
    this.stop$.complete();
  }

  private getERAandRentalCurrentDeductible(code: string, vehicle: RiskModel): boolean {
    return _get(vehicle, 'vehicleCoverages', []).find(coverage => coverage.code === code);
  }

  private getCoverageLimit(code: string, vehicle: RiskModel | Vehicle): string {
    const matchingCoverage = _get(vehicle, 'vehicleCoverages', []).find(
      coverage => coverage.code === code
    );

    const limits = {
      dailyLimit: this.getTermLimitByDescription(matchingCoverage, 'Daily Limit'),
      maxLimit: this.getTermLimitByDescription(matchingCoverage, 'Maximum'),
      perPersonLimit: this.getTermLimitByDescription(matchingCoverage, 'Per Person'),
      perOccurrenceLimit: this.getTermLimitByDescription(matchingCoverage, 'Per Occurrence'),
      deductableLimit: this.getDeductableLimitByDescription(matchingCoverage, 'Deductible'),
      compDeductibleLimit: this.getDeductableLimitByDescription(
        matchingCoverage,
        'Comprehensive Deductible'
      )
    };

    if (limits.dailyLimit && limits.maxLimit) {
      return `$${limits.dailyLimit?.value} Daily Limit, ${limits.maxLimit?.value} Maximum`;
    }

    if (limits.perPersonLimit && limits.perOccurrenceLimit) {
      return `$${limits.perPersonLimit?.value}/${limits.perOccurrenceLimit?.value}/ Per Person/ Per Occurrence`;
    }

    if (limits.perOccurrenceLimit) return `$${limits.perOccurrenceLimit?.value}`;

    if (limits.deductableLimit) return `$${limits.deductableLimit?.value}`;
    if (limits.compDeductibleLimit) return `$${limits.compDeductibleLimit?.value}`;

    return '';
  }

  private getTermLimitByDescription(coverage: any, desc: string) {
    return _get(coverage, 'deductibleTerms', []).find(
      ({ description }) => !!(description === desc)
    );
  }

  private getDeductableLimitByDescription(coverage: any, desc: string) {
    return _get(coverage, 'deductibleTerms', []).find(
      ({ description }) => !!(description === desc)
    );
  }
}
